import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import type { Chapter } from "../../types";
import { PanelHeader } from "./PanelHeader";
import { ExtractToolbar } from "./ExtractToolbar";
import { EmptyState } from "../ui/empty-state";
import { Field } from "../ui/field";
import { SectionCard } from "../ui/section-card";
import { IconOpenIn, IconRemove } from "./icons";
import {
  LOCATION_TYPE_LABELS,
  appearanceForChapter,
  locationsInChapter,
  type BookLocation,
  type LocationType,
} from "../../../shared/locationRoster";

function ChapterLocationCard({
  location,
  chapter,
  roster,
  onUpdateLocation,
  onOpenInGuide,
  onRequestRemove,
}: {
  location: BookLocation;
  chapter: Chapter;
  roster: BookLocation[];
  onUpdateLocation: (id: string, patch: Partial<BookLocation>) => void;
  onOpenInGuide: (id: string) => void;
  onRequestRemove: (location: BookLocation) => void;
}) {
  const appearance = appearanceForChapter(location, chapter.id);
  const parent = location.parentLocationId
    ? roster.find((loc) => loc.id === location.parentLocationId)
    : undefined;

  function updateNote(note: string): void {
    const other = location.appearances.filter((a) => a.chapterId !== chapter.id);
    onUpdateLocation(location.id, {
      appearances: [
        ...other,
        {
          chapterId: chapter.id,
          ...(note.trim() ? { note: note.trim() } : {}),
        },
      ],
    });
  }

  return (
    <SectionCard
      bodyClassName="flex flex-col gap-2.5"
      title={location.name}
      actions={
        <>
          <Select
            value={location.type}
            onValueChange={(value) => onUpdateLocation(location.id, { type: value as LocationType })}
          >
            <SelectTrigger className="min-w-26" size="sm" aria-label={`${location.name} type`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(Object.keys(LOCATION_TYPE_LABELS) as LocationType[]).map((type) => (
                <SelectItem key={type} value={type}>
                  {LOCATION_TYPE_LABELS[type]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            size="icon-sm"
            variant="ghost"
            type="button"
            aria-label="Open in Locations"
            title="Open in Locations"
            onClick={() => onOpenInGuide(location.id)}
          >
            <IconOpenIn />
          </Button>
          <Button
            size="icon-sm"
            variant="destructive"
            type="button"
            aria-label="Remove location"
            title="Remove location"
            onClick={() => onRequestRemove(location)}
          >
            <IconRemove />
          </Button>
        </>
      }
    >
      {location.aliases.length > 0 ? (
        <p className="text-xs text-muted-foreground">Also: {location.aliases.join(", ")}</p>
      ) : null}
      <p className="text-xs text-muted-foreground">
        {LOCATION_TYPE_LABELS[location.type]}
        {parent ? ` · in ${parent.name}` : ""}
      </p>

      <Field label="What this place is (book)">
        <Textarea
          aria-label={`${location.name} description`}
          className="min-h-0! text-[0.8rem]!"
          placeholder="Lasting place identity: layout, atmosphere, role…"
          value={location.description}
          rows={2}
          onChange={(e) => onUpdateLocation(location.id, { description: e.target.value })}
        />
      </Field>

      <Field label="How it appears this chapter">
        <Textarea
          aria-label={`${location.name} chapter note`}
          className="min-h-0! text-[0.8rem]!"
          placeholder="Scene-specific details for this chapter only…"
          value={appearance?.note ?? ""}
          rows={2}
          onChange={(e) => updateNote(e.target.value)}
        />
      </Field>
    </SectionCard>
  );
}

export function ChapterLocationsPanel({
  activeChapter,
  roster,
  busy,
  extractModel,
  onExtractModelChange,
  openAiModels,
  anthropicModels,
  onExtract,
  onUpdateLocation,
  onAddLocation,
  onOpenInGuide,
  onRequestRemove,
}: {
  activeChapter: Chapter;
  roster: BookLocation[];
  busy: boolean;
  extractModel: string;
  onExtractModelChange: (model: string, provider: "openai" | "anthropic") => void;
  openAiModels: ReadonlyArray<{ value: string; label: string }>;
  anthropicModels: ReadonlyArray<{ value: string; label: string }>;
  onExtract: () => void;
  onUpdateLocation: (id: string, patch: Partial<BookLocation>) => void;
  onAddLocation: () => void;
  onOpenInGuide: (id: string) => void;
  onRequestRemove: (location: BookLocation) => void;
}) {
  const inChapter = locationsInChapter(roster, activeChapter.id);

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      <PanelHeader
        title="Locations in this chapter"
        description="Significant settings and rooms. Extract merges into the book gazetteer."
        actions={
          <ExtractToolbar
            model={extractModel}
            onModelChange={onExtractModelChange}
            openAiModels={openAiModels}
            anthropicModels={anthropicModels}
            onExtract={onExtract}
            extractBusy={busy}
            extractLabel="Extract locations"
            onAdd={onAddLocation}
            addLabel="Add location"
          />
        }
      />

      <div className="flex min-h-0 flex-1 flex-col gap-2.5 overflow-y-auto px-4 pt-3.5 pb-5">
        {inChapter.length === 0 ? (
          <EmptyState
            title="No locations linked to this chapter yet."
            description="Run Extract locations to pull significant settings from the manuscript, or add one manually."
          />
        ) : (
          inChapter.map((location) => (
            <ChapterLocationCard
              key={location.id}
              location={location}
              chapter={activeChapter}
              roster={roster}
              onUpdateLocation={onUpdateLocation}
              onOpenInGuide={onOpenInGuide}
              onRequestRemove={onRequestRemove}
            />
          ))
        )}
      </div>
    </div>
  );
}
